<!-- src/components/VideoUploadItem.vue -->
<template>
  <div class="video-upload-item">
    <div v-if="showFileName" class="file-name-hint">
      <span class="hint-label">文件名：</span>
      <span class="file-name">{{ fileName }}</span>
    </div>

    <a-upload
      :before-upload="handleFileChange"
      accept="video/*"
      :show-upload-list="false"
    >
      <a-button :type="showFileName || uploadStatus === 'success' ? 'default' : 'primary'" :loading="uploading">
        <a-icon type="upload" />
        {{ showFileName || uploadStatus === 'success' ? '重新上传' : '选择视频文件' }}
      </a-button>
    </a-upload>

    <div v-if="uploadStatus === 'error'" class="upload-error">
      <a-icon type="close-circle" style="color: #ff4d4f; margin-right: 8px;" />
      <span>{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
/**
 * 单个视频上传组件
 * 用于上传特定动作组合的视频
 */
import { Upload, Button, Icon, message } from 'ant-design-vue'
import { uploadVideoWithInfo } from '@/api/faceRecognition'

// 动作标签映射
const actionLabelMap = {
  'nod_shake': '点头-摇头',
  'nod_openMouth': '点头-张嘴',
  'nod_blink': '点头-眨眼',
  'shake_nod': '摇头-点头',
  'shake_openMouth': '摇头-张嘴',
  'shake_blink': '摇头-眨眼',
  'openMouth_nod': '张嘴-点头',
  'openMouth_shake': '张嘴-摇头',
  'openMouth_blink': '张嘴-眨眼',
  'blink_nod': '眨眼-点头',
  'blink_shake': '眨眼-摇头',
  'blink_openMouth': '眨眼-张嘴'
}

/**
 * 根据 actionKey 生成文件名
 * 格式：{action1}_{action2}.mp4
 * @param {string} actionKey - 动作组合的 key，如 'nod-openMouth'
 * @returns {string} 文件名，如 'nod_openMouth.mp4'
 */
function generateFileName(actionKey) {
  // 将 key 中的 '-' 替换为 '_'，并添加 .mp4 后缀
  return `${actionKey.replace(/-/g, '_')}.mp4`
}

export default {
  name: 'VideoUploadItem',
  components: {
    'a-upload': Upload,
    'a-button': Button,
    'a-icon': Icon
  },
  props: {
    actionKey: {
      type: String,
      required: true
    },
    fileNameList: {
      type: Array,
      default: () => []
    },
    phoneNumber: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      uploading: false,
      uploadStatus: '', // 'success' | 'error' | ''
      errorMessage: ''
    }
  },
  computed: {
    /**
     * 动作组合的显示标签
     */
    actionLabel() {
      return actionLabelMap[this.actionKey] || this.actionKey
    },
    /**
     * 自动生成的文件名
     * 格式：{action1}_{action2}.mp4
     */
    fileName() {
      return generateFileName(this.actionKey)
    },
    /**
     * 是否显示文件名
     * 只有当 fileNameList 中包含当前 actionKey 对应的文件名时才显示
     */
    showFileName() {
      if (!this.fileNameList || !Array.isArray(this.fileNameList) || this.fileNameList.length === 0) {
        return false
      }
      return this.fileNameList.includes(this.fileName)
    }
  },
  watch: {
    /**
     * 监听手机号变化，重置上传状态
     */
    phoneNumber() {
      this.uploadStatus = ''
      this.errorMessage = ''
    },
    /**
     * 监听 showFileName 变化，当文件名显示后重置上传状态
     * 这样上传成功后，文件名会显示，按钮会显示"重新上传"
     */
    showFileName(newVal) {
      if (newVal && this.uploadStatus === 'success') {
        // 使用 $nextTick 确保 DOM 更新后再重置状态
        this.$nextTick(() => {
          this.uploadStatus = ''
        })
      }
    }
  },
  methods: {
    /**
     * 处理文件上传
     * @param {File} file - 上传的文件
     * @returns {boolean} - 返回 false 阻止默认上传行为
     */
    async handleFileChange(file) {
      if (!file) return false

      // 验证手机号
      if (!this.phoneNumber || !/^1[3-9]\d{9}$/.test(this.phoneNumber)) {
        message.error('请先输入正确的手机号')
        return false
      }

      this.uploading = true
      this.uploadStatus = ''
      this.errorMessage = ''

      try {
        // 使用自动生成的文件名
        const response = await uploadVideoWithInfo(
          this.phoneNumber,
          this.fileName,
          file
        )

        // 根据文档，检查返回结果
        if (response.data && response.data.success) {
          this.uploadStatus = 'success'
          message.success(`${this.actionLabel} 视频上传成功`)
          // 传递文件名，让父组件更新 fileNameList
          this.$emit('upload-success', this.actionKey, response.data.data, this.fileName)
        } else {
          const errorMsg = response.data?.message || '上传失败'
          this.uploadStatus = 'error'
          this.errorMessage = errorMsg
          message.error(`${this.actionLabel} 上传失败: ${errorMsg}`)
          this.$emit('upload-error', this.actionKey, new Error(errorMsg))
        }
      } catch (err) {
        this.uploadStatus = 'error'
        
        // 优化错误提示信息
        let errorMsg = '上传失败'
        if (err.response) {
          if (err.response.status === 500) {
            const responseData = err.response.data || ''
            if (responseData.includes('ECONNREFUSED') || responseData.includes('Proxy error')) {
              errorMsg = '无法连接到后端服务器，请确保后端服务已启动'
            } else {
              errorMsg = `服务器错误: ${responseData}`
            }
          } else {
            errorMsg = `请求失败 (${err.response.status}): ${err.response.data?.message || err.message}`
          }
        } else if (err.request) {
          errorMsg = '无法连接到服务器，请检查网络连接'
        } else {
          errorMsg = err.message || '未知错误'
        }
        
        this.errorMessage = errorMsg
        message.error(`${this.actionLabel} 上传失败: ${errorMsg}`)
        this.$emit('upload-error', this.actionKey, err)
      } finally {
        this.uploading = false
      }

      // 返回 false 阻止默认上传行为
      return false
    }
  }
}
</script>

<style scoped>
.video-upload-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-name-hint {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
}

.hint-label {
  color: #666;
  margin-right: 8px;
}

.file-name {
  color: #1890ff;
  font-weight: 500;
  font-family: 'Courier New', monospace;
}

.upload-success,
.upload-error {
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-top: 8px;
}

.upload-error {
  color: #ff4d4f;
}
</style>

